import React, { useCallback, useEffect, useState } from 'react'

import api from 'src/api'
import { Provider } from 'src/api/gen/api'

interface IProps {
  children?: React.ReactNode
}

export interface IProviderContext {
  activeProviders: Provider[]
  activeProvidersIds: string[]
  addProvider?: (provider: Provider) => void
  allProviders: Provider[]
  colors: Record<string, string>
  isLoaded: boolean
  providersMapping: Map<string, Provider>
  updateProvider?: (provider: Provider) => void
}

const initialProviderContextValue: IProviderContext = {
  activeProviders: [],
  activeProvidersIds: [],
  allProviders: [],
  colors: {},
  isLoaded: false,
  providersMapping: new Map(),
}

export const ProviderContext = React.createContext<IProviderContext>(initialProviderContextValue)

const ProviderWrapper = (props: IProps) => {
  const [providerContextValue, setProviderContextValue] = useState<IProviderContext>(initialProviderContextValue)
  const addProvider = useCallback(
    function (provider: Provider) {
      setProviderContextValue((prevState) => ({
        ...prevState,
        ...computeProvidersAttributes(prevState.allProviders.concat([provider])),
      }))
    },
    [setProviderContextValue]
  )
  const updateProvider = useCallback(
    function (providerToUpdate: Provider) {
      setProviderContextValue((prevState) => ({
        ...prevState,
        ...computeProvidersAttributes(
          prevState.allProviders.map((provider) => (provider.id === providerToUpdate.id ? providerToUpdate : provider))
        ),
      }))
    },
    [setProviderContextValue]
  )

  useEffect(() => {
    fetchProvidersList().then((fetchedProviders) => {
      setProviderContextValue({
        ...computeProvidersAttributes(fetchedProviders),
        addProvider: addProvider,
        updateProvider: updateProvider,
        isLoaded: true,
      })
    })
  }, [])

  return <ProviderContext.Provider value={providerContextValue}>{props.children}</ProviderContext.Provider>
}

async function fetchProvidersList(): Promise<Provider[]> {
  try {
    return await api.providers.providersList()
  } catch (error) {
    console.error(error)
    return []
  }
}

export function computeProvidersAttributes(
  updatedProviders: Provider[]
): Omit<IProviderContext, 'addProvider' | 'updateProvider' | 'isLoaded'> {
  return {
    allProviders: updatedProviders,
    activeProviders: updatedProviders.filter((provider) => provider.is_active),
    activeProvidersIds: updatedProviders.filter((provider) => provider.is_active).map((provider) => provider.id),
    colors: getProvidersColorsMapping(updatedProviders),
    providersMapping: new Map(updatedProviders.map((provider) => [provider.id, provider])),
  }
}

export function getProvidersColorsMapping(providers: Provider[]): Record<string, string> {
  const mapping: Record<string, string> = {}

  providers.forEach((provider: Provider) => {
    if (provider.colors && provider.colors.primary) {
      mapping[provider.name] = provider.colors.primary
    }
  })

  return mapping
}

export default ProviderWrapper
